import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getMe, login as apiLogin, logout as apiLogout, silentRefresh, type AuthUser, type LoginArgs } from '../api/auth.js';
import { subscribeToAccessToken, getAccessToken } from './tokenStore.js';

/**
 * Auth React state. Exposes:
 *   - `user` — the logged-in user, or null when unauthenticated
 *   - `status` — 'loading' on first mount (during silent refresh), then
 *     'authenticated' or 'unauthenticated'
 *   - `login(args)` / `logout()` — wrappers around the API calls
 *
 * On mount, we always fire silentRefresh() once. If the user has a valid
 * refresh cookie, they land already logged in. If not, status goes to
 * 'unauthenticated' and the router sends them to /login.
 *
 * We deliberately do NOT persist the user object — it lives only in React
 * state. If the page reloads, we re-fetch via the refresh flow (future:
 * GET /v1/users/me to repopulate the user; for now the FE only needs the
 * token for API calls, user fields are re-fetched on-demand).
 */

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /**
   * Resolves with the freshly-fetched user. The caller (login page)
   * uses this to route by role without waiting for a setState +
   * rerender + useEffect cycle. State is still updated for everything
   * else that subscribes via useAuth().
   */
  login: (args: LoginArgs) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * `initialUser` short-circuits silentRefresh for tests / storybook. When
 * provided, the provider starts in the 'authenticated' state with that
 * user and skips the one-shot mount refresh. Real runtime code must NOT
 * pass this — the value would bypass the actual auth handshake.
 */
export function AuthProvider({
  children,
  initialUser,
}: {
  children: ReactNode;
  initialUser?: AuthUser;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(initialUser ?? null);
  const [status, setStatus] = useState<AuthStatus>(
    initialUser ? 'authenticated' : 'loading',
  );

  // Keep status in sync with the imperative token store so a 401-triggered
  // logout from anywhere in the app (e.g. refresh failure inside apiRequest)
  // flips the UI to unauthenticated without needing a prop chain. The
  // cache clear here covers the involuntary-logout path — the explicit
  // login/logout callbacks above also clear, but those don't fire when
  // apiRequest force-clears the token from underneath us (e.g. refresh
  // failure mid-session).
  useEffect(() => {
    return subscribeToAccessToken((token) => {
      if (!token) {
        queryClient.clear();
        setUser(null);
        setStatus('unauthenticated');
      }
    });
  }, [queryClient]);

  // One-shot silent refresh on mount. If the refresh cookie is valid we
  // follow up with GET /v1/users/me to rehydrate the user object — the
  // refresh endpoint only returns an access token, so without this call
  // the header would flash blank ("· role") on every page reload.
  useEffect(() => {
    if (initialUser) return; // Seeded — don't overwrite in tests/storybook.
    let cancelled = false;
    void (async () => {
      const ok = await silentRefresh();
      if (cancelled) return;
      if (!ok) {
        setStatus('unauthenticated');
        return;
      }
      try {
        const me = await getMe();
        if (cancelled) return;
        setUser(me);
        setStatus('authenticated');
      } catch {
        // If /me fails for any reason (expired token, server down, etc.)
        // treat it as unauthenticated and let the router bounce to /login.
        // Better to show the login page than a half-hydrated dashboard.
        if (cancelled) return;
        setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialUser]);

  const login = useCallback(
    async (args: LoginArgs): Promise<AuthUser> => {
      // Belt-and-suspenders against cross-user cache leakage: if a
      // previous session left cached queries in memory (the user closed
      // the tab without explicitly signing out), wipe them BEFORE the
      // first authenticated query of the new session fires.
      queryClient.clear();
      const u = await apiLogin(args);
      setUser(u);
      setStatus('authenticated');
      return u;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      // Always clear local state — even if apiLogout's network call
      // failed, the user clicked "Sign out" and expects their data
      // gone from this tab. The tokenStore subscription above ALSO
      // clears on the clearAccessToken() call inside apiLogout, but
      // clearing twice is harmless (and protects the case where
      // apiLogout throws before reaching clearAccessToken).
      queryClient.clear();
      setUser(null);
      setStatus('unauthenticated');
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout }),
    [status, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

/** Imperative check used by the router beforeLoad gate. */
export function isAuthenticated(): boolean {
  return getAccessToken() !== null;
}
